import { adjustedRetryTimeout, clearObj, asyncTimeout, lazyLogger } from "@gotti/core";
import { PubSub } from "./PubSub.js";
import { SERVER_SERVER_PROTOCOLS, SERVER_TYPES } from "./constants.js";
import { assertServerKeyPair, derivePublicKey, generateServerKeyPair, isCurveKey } from "./trust/keys.js";
import { DEFAULT_CLUSTER_ID } from "./trust/ipcDir.js";

/**
 * Module-scope, NOT a `logger` field: `GateServer`, `MasterServer` and
 * `AreaServer` each declare their own `readonly logger`, and adding one here
 * would shadow every subclass's prefix.
 */
const baseLogger = lazyLogger("BaseServer:", { kind: 'server' });

export enum ServerConnectionStatus {
    CONNECTED,
    PENDING_REGISTRATION_CONFIRMATION,
    AWAITING_REGISTRATION_REQUEST,
}

export type ServerHeartbeats = {
    lastSentPing: number,
    lastSentPong: number,
    lastReceivedPing: number,
    lastReceivedPong: number,
    lastState?: any
}

export type RegisteredServerParams = {
    id: string,
    type: string,
}

export enum RemoveConnectionReason {
    STOPPED,
    MISSED_HEARTBEAT,
    FAILED_TO_REGISTER,
}

export type RegisteredServerData<T> = BaseServerConfig<T> & {
    preparedFor: boolean
    heartbeats: ServerHeartbeats,
    status: ServerConnectionStatus,
    connectedCallback?: (d: RegisteredServerData<T>) => void,
}


export type BaseServerConfig<T> = {
    /**
     * The cluster this server belongs to. Part of the registration handshake:
     * a peer from another cluster is refused. Unset = `'default'` (hand-wired
     * servers and tests); a Starter always stamps its cluster's id.
     */
    clusterId?: string,
    /**
     * This server's CurveZMQ public key (Z85). Shared with peers — it is how
     * they pin this server. On a PEER descriptor (the config handed to
     * `prepareForServerRegistrationRequest` / `sendRegistrationRequest`) it is
     * REQUIRED: no key, no connection.
     */
    publicKey?: string,
    /**
     * This server's CurveZMQ secret key (Z85). Private to this process: never
     * in `sharedConfig`. When neither key is set the server generates a keypair
     * in memory at construction.
     */
    secretKey?: string,
    /**
     * The cluster secret join tokens are signed with (master only). Private:
     * never in `sharedConfig`.
     */
    clusterSecret?: string,
    inProcessOnly?: boolean,
    host: string,
    port: number,
    /** Interface this server's listeners bind (see `GottiServerConfig.bindHost`); unset = historical per-listener default. */
    bindHost?: string,
    id: string | number,
    type: SERVER_TYPES,
    requestTimeout?: number,
    options: T,
    redis?: RedisConnectionData
    forceTcp?: boolean
}

export type ServerProtocolArray = [SERVER_SERVER_PROTOCOLS, ...any[]]

const HEARTBEAT_TIMEOUT = 15000;

type PrepareConnectionOptions = {
    timeout: number
}

const DefaultPrepareOptions : PrepareConnectionOptions = {
    timeout: 60000
}
import { RedisClientWrapper, RedisConnectionData } from './RedisClientWrapper.js';
export abstract class BaseServer {
    private seq : number = 0;
    readonly requestCbs : {[requestId: string]: (responseData: any, error?: string) => void  } = {};

    readonly pubSub? : PubSub;
    private requestTimeout : number;
    readonly serverConnections : {[serverId: string]: RegisteredServerData<any>} = {};
    readonly connectionsByType: {[type: string]: RegisteredServerData<any>[] } = {};

    readonly localServers : {[serverId: string]: BaseServer} = {};

    private connectionMadeListeners : Array<(d: RegisteredServerData<any>, isLocal: boolean) => void> = [];
    private connectionCloseListeners : Array<(d: RegisteredServerData<any>, reason: RemoveConnectionReason) => void> = [];

    private closed : boolean = false;

    public onHeartbeat?() : any;

    public async onBeforeInit?() : Promise<void>;
    public onMasterBroadcast?(data: any) : Promise<void> | void;
    public async onAfterInit?() : Promise<void>;

    private hbInterval : NodeJS.Timeout;
    readonly config : BaseServerConfig<any>;
    readonly id: string | number;

    public redis?: RedisClientWrapper;

    /**
     * THIS SERVER'S CONFIG AS EVERY OTHER SERVER IS ALLOWED TO SEE IT: identical
     * to `config` except that the redis connection is not on it.
     *
     * *** THE INVARIANT: `this.config` is PRIVATE to this process. Anywhere this
     * server's own config is handed to ANOTHER server — published on the wire or
     * passed to a co-located server's handler — it goes as `sharedConfig`. ***
     *
     * This exists because registration publishes the SENDER'S ENTIRE CONFIG:
     * `sendRegistrationRequest` sends `[REGISTER_SERVER, this.id, this.config,
     * ...]` and `onNewRegisterServer` answers `[CONFIRM_REGISTRATION, this.id,
     * this.config, ...]`. The `d` argument those two take is only the TARGET
     * DESCRIPTOR — the address of the server being registered WITH — so keeping
     * a secret off `d` protects nothing. The receiver stores what it is sent
     * verbatim (`finalizeConnection` spreads `...d` into `serverConnections`),
     * the transport is plain `tcp://` with no CURVE or TLS, and nothing anywhere
     * reads a PEER's `redis` — the only reader of the field is `initRedis`, on
     * the server that owns it. So a configured gate credential would otherwise
     * reach the master and every connector, and a master's would reach every
     * connector, every area and the gate.
     *
     * WHY A SHARED-VIEW ACCESSOR RATHER THAN REMOVING `redis` FROM THE CONFIG
     * TYPE. Taking it off `BaseServerConfig` and passing it some other way looks
     * stronger — you cannot leak what is not there — but it is both narrower and
     * riskier. Narrower: the defect is "a server publishes its WHOLE config to
     * its peers", so the next credential-shaped field added to a config (a TLS
     * key path, an auth key) leaks exactly the same way; a named boundary that
     * decides what a peer may see is the thing that generalises, and it is the
     * place to extend. Riskier: `redis` is on the PUBLIC `GateServerConfig` /
     * `MasterServerConfig` types that games' own gate/master subclasses are
     * constructed with, and `initRedis` reads `this.config.redis`; removing it
     * would silently downgrade every existing consumer that passes it to memory
     * mode — the worst failure this feature has, because nothing would report it.
     *
     * Memoized on first use — the config is never mutated after construction
     * (verified by grep) — so this is a stable object rather than a per-call
     * allocation on the message-relay paths. Lazy rather than built in the
     * constructor specifically so `redactSharedOptions` can be overridden by a
     * subclass: the first read happens at registration, long after subclass
     * fields exist.
     *
     * WHAT IT DOES **NOT** COVER, stated so it is not mistaken for a general
     * secret boundary: `options` is a per-server-type blob, and only the type
     * that owns it knows which parts are secret. That is what
     * `redactSharedOptions` is for; a server type with secrets in its options
     * MUST override it. Nothing outside the config object is covered at all —
     * notably `gotti deploy`, which writes the project config to each host's
     * disk over ssh and does its own scoping (`Deployer.copyProjectConfig`).
     */
    private _sharedConfig?: BaseServerConfig<any>;
    get sharedConfig(): BaseServerConfig<any> {
        if(!this._sharedConfig) {
            // `secretKey` / `clusterSecret` are this server's own credentials —
            // the same class of field as `redis`, stripped at the same boundary.
            // `publicKey` and `clusterId` stay: they are what peers pin.
            const { redis, options, secretKey, clusterSecret, ...shareable } = this.config;
            this._sharedConfig = {
                ...shareable,
                options: this.redactSharedOptions(this.config.options),
            } as BaseServerConfig<any>;
        }
        return this._sharedConfig;
    }

    /** This server's CurveZMQ public key (Z85) — what peers pin it by. */
    get publicKey(): string {
        return this.config.publicKey!;
    }

    /** The cluster this server belongs to (`'default'` when unset). */
    get clusterId(): string {
        return this.config.clusterId || DEFAULT_CLUSTER_ID;
    }

    /**
     * Resolve this server's CurveZMQ identity: the configured keypair (checked
     * to belong together), a public key derived from a lone secret key, or —
     * with neither set — a keypair generated here, in memory. A public key
     * with no secret key cannot run anything and is refused.
     */
    static resolveIdentity(config: BaseServerConfig<any>): { publicKey: string, secretKey: string } {
        const { publicKey, secretKey } = config;
        if(publicKey === undefined && secretKey === undefined) return generateServerKeyPair();
        if(secretKey === undefined) {
            throw new Error(`Server '${config.id}' has a publicKey but no secretKey — a server needs its own secret key to run.`);
        }
        if(publicKey === undefined) {
            if(!isCurveKey(secretKey)) throw new Error(`Server '${config.id}': secretKey is not a 40-character Z85 CurveZMQ key.`);
            return { publicKey: derivePublicKey(secretKey), secretKey };
        }
        return assertServerKeyPair(config.id, { publicKey, secretKey });
    }

    /**
     * Strip anything secret out of THIS server type's `options` before a peer
     * sees it. Default: nothing to strip.
     *
     * Override it when a server type's options carry a credential. Peers read
     * very little off each other's options and what they do read is structural
     * (the gate uses `'webRtcOptions' in options` purely as a protocol
     * discriminator, then takes host/port from `websocketOptions`), so a
     * redaction here is almost always free — but it must PRESERVE THE SHAPE
     * those structural reads depend on rather than deleting keys wholesale.
     *
     * Called once, lazily, from `sharedConfig`.
     */
    protected redactSharedOptions(options: any): any {
        return options;
    }

    readonly finalizedConnections : Array<string | number> = [];

    constructor(config: BaseServerConfig<any>) {
        // The identity is resolved BEFORE anything can use the config, and the
        // server keeps its own copy carrying it (the caller's object is not
        // mutated — it may be a shared template).
        config = { ...config, ...BaseServer.resolveIdentity(config) };
        if(!config.inProcessOnly) {
            this.pubSub = new PubSub(config, this.onSubscriptionMessage.bind(this))
        }
        this.config = config;
        this.id = `${config.id}`;
        this.requestTimeout = config.requestTimeout || 15000;
        this.hbInterval = setInterval(this.doHeartbeat.bind(this), HEARTBEAT_TIMEOUT);
    }

    get heartbeatData() {
        return this.onHeartbeat?.();
    }

    /**
     * BOTH BRANCHES LOG. This feature's worst failure mode is a SILENT
     * DOWNGRADE: a server that was meant to run on the shared redis store
     * quietly running its own in-process one instead, behaving perfectly in a
     * single-process test and wrongly in a cluster. It has already happened
     * three independent ways during this work (a dropped key prefix, a `redis`
     * option that had to stay on `BaseServerConfig` precisely so removing it
     * could not downgrade every existing consumer, and deploy-time host-string
     * scoping), and in every one of them nothing said a word at boot.
     *
     * One line at the decision point makes every variant obvious in production.
     * The per-server-type STORE selection is logged by the gate and the master
     * on top of this (see their `onAfterInit`) — this one covers the handle
     * itself, for every server type.
     */
    private async initRedis() {
        // `SERVER_TYPES` is a NUMERIC enum, so `this.config.type` is a bare
        // ordinal (e.g. `2`) — logging it directly prints an unreadable line
        // like "BaseServer: 2 gate-1: redis connected." TS numeric enums carry
        // a reverse mapping (`SERVER_TYPES[2] === 'GATE'`), so this reads the
        // name back out rather than adding a second lookup table to keep in
        // sync with `constants.ts`.
        const typeName = SERVER_TYPES[this.config.type]?.toLowerCase() ?? String(this.config.type);
        if (this.config.redis) {
            this.redis = await RedisClientWrapper.Init(this.config.redis);
            baseLogger.info(`${typeName} ${this.id}: redis connected.`);
        } else {
            baseLogger.info(`${typeName} ${this.id}: no redis configured — all state is in-process.`);
        }
    }

    public async initialize() {
        this.closed = false;
        await this.onBeforeInit?.();

        // UNCONDITIONAL: `initRedis` carries the same `if(this.config.redis)`
        // this used to duplicate, and it is where both outcomes are now logged
        // — a second guard out here would silence the memory-mode line.
        await this.initRedis();

        await this.onAfterInit?.();
    }

    public async close() {
        clearObj(this.serverConnections);
        clearObj(this.localServers);
        this.closed = true;
        clearInterval(this.hbInterval);
        if(!this.config.inProcessOnly) {
            try {
                await this.pubSub?.close();
            } catch (err) {
                console.error("Error when closing pub sub:", err instanceof Error ? err.message : String(err));
            }
        }
    }

    public isLocalServer(id: string | number) {
        return id in this.localServers;
    }
    public addLocalServers(ds: BaseServer[]) {
        ds.forEach(d => this.addLocalServer(d));
    }

    public addLocalServer(d: BaseServer) {
        //console.log('add local server', d.id);
        let finalizeOurConnection = false;
        let finalizeServerConnection = false;
        if(!(d.id in this.localServers)) {
            finalizeOurConnection = true;
            this.localServers[d.id] = d;
        }
        if(!(this.config.id in d.localServers)) {
            finalizeServerConnection = true;
            d.localServers[this.config.id] = this;
        }

        if(finalizeOurConnection) {
            //console.log('yo')
            this.finalizeConnection(d.sharedConfig, d.heartbeatData, true);
        }
        if(finalizeServerConnection) {
            //console.log('er');
            d.finalizeConnection(this.sharedConfig, this.heartbeatData, true);
        }
        //console.log('finalized');
    }

    public removeLocalServer(d: BaseServer) {
        delete this.localServers[d.id];
        if(d.localServers) {
            delete d.localServers[this.id];
        }
    }

    public onConnectionClosed(cb: (d: RegisteredServerData<any>, reason: RemoveConnectionReason) => void) {
        this.connectionCloseListeners.push(cb);
    }
    public offConnectionClosed(cb: (d: RegisteredServerData<any>, reason: RemoveConnectionReason) => void) {
        const idx = this.connectionCloseListeners.indexOf(cb);
        if(idx > -1) {
            this.connectionCloseListeners.splice(idx, 1);
        }
    }
    
    public onConnectionMade(cb: (d: RegisteredServerData<any>, isLocal: boolean) => void) {
        this.connectionMadeListeners.push(cb);
    }
    public offConnectionMade(cb: (d: RegisteredServerData<any>, isLocal: boolean) => void) {
        const idx = this.connectionMadeListeners.indexOf(cb);
        if(idx > -1) {
            this.connectionMadeListeners.splice(idx, 1);
        }
    }

    /** Messages dropped because the payload's sender disagreed with the socket it arrived on, or a role check failed. */
    public refusedMessages = 0;

    private refuse(reason: string) {
        this.refusedMessages++;
        baseLogger.error(`[${this.id}] REFUSED: ${reason}`);
    }

    /**
     * Generic protocols carry no server-type-specific handler that could check
     * the sender's role, so the role is checked here. The role is the PREPARED
     * type of the connection (see `finalizeConnection`), never a payload field.
     */
    private static readonly GENERIC_PROTOCOL_ROLES: Partial<Record<SERVER_SERVER_PROTOCOLS, SERVER_TYPES[]>> = {
        [SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE_FAST_RELAY_TO_CLIENT]: [SERVER_TYPES.AREA],
        [SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE_FAST_RELAY_TO_CLIENTS]: [SERVER_TYPES.AREA],
        [SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE_FAST_RELAY_TO_AREA]: [SERVER_TYPES.AREA],
        [SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE_FAST_RELAY_TO_CHANNEL]: [SERVER_TYPES.AREA],
        [SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE_FAST_RELAY_RAW_TO_AREA]: [SERVER_TYPES.AREA],
        [SERVER_SERVER_PROTOCOLS.MASTER_BROADCAST]: [SERVER_TYPES.MASTER],
    };

    /**
     * `fromPeerId` is the peer whose CURVE-pinned subscriber delivered this
     * message — the AUTHENTICATED sender. The payload's own sender slot
     * (`parsed[1]`) must agree with it; a message claiming to be from someone
     * else is an impersonation attempt (or a badly broken peer) and is dropped.
     */
    private async onSubscriptionMessage(parsed: ServerProtocolArray, fromPeerId: string) {
        // `parsed` is already the decoded envelope array — PubSub reconstructs it
        // from the subscription frames (a plain 2-frame JSON message, or a
        // 3-frame relay whose trailing payload frame is appended verbatim).
        const protocol : SERVER_SERVER_PROTOCOLS = parsed[0];
        const id : string = parsed[1];
        if(`${id}` !== `${fromPeerId}`) {
            this.refuse(`message (protocol ${protocol}) arrived on peer '${fromPeerId}''s socket claiming to be from '${id}' — dropped.`);
            return;
        }
        const server = this.serverConnections[fromPeerId];
        if(!server) {
            return;
        };
        const roles = BaseServer.GENERIC_PROTOCOL_ROLES[protocol];
        if(roles && !roles.includes(server.type)) {
            this.refuse(`protocol ${protocol} from '${fromPeerId}' (type ${SERVER_TYPES[server.type] ?? server.type}) is reserved for ${roles.map(r => SERVER_TYPES[r]).join('/')} — dropped.`);
            return;
        }
        switch(protocol) {
            case SERVER_SERVER_PROTOCOLS.HEARTBEAT_PING: {
                server.heartbeats.lastState = parsed[2];
                this.sendPong(id);
                break;
            }
            case SERVER_SERVER_PROTOCOLS.HEARTBEAT_PONG: {
                server.heartbeats.lastReceivedPong = Date.now();
                server.heartbeats.lastState = parsed[2];
                break;
            }
            case SERVER_SERVER_PROTOCOLS.CONFIRM_REGISTRATION:
            case SERVER_SERVER_PROTOCOLS.REGISTER_SERVER: {
                const msgData : any = parsed[2];
                const pingState : any = parsed[3];
                // The config inside must describe the peer whose socket this is —
                // otherwise a peer could register "as" another prepared entry.
                if(`${msgData?.id}` !== `${fromPeerId}`) {
                    this.refuse(`registration message on peer '${fromPeerId}''s socket describes server '${msgData?.id}' — dropped.`);
                    break;
                }
                if(protocol === SERVER_SERVER_PROTOCOLS.CONFIRM_REGISTRATION) {
                    this.onConfirmRegistration(msgData, pingState);
                } else {
                    this.onNewRegisterServer(msgData, pingState);
                }
                break;
            }
            // PREPARE_FOR_SERVER_REGISTRATION is deliberately NOT handled: it made
            // this server connect to (and trust) any address a peer named, and
            // nothing in the code base ever sent it. The ordinal stays reserved.
            case SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE_FAST_RELAY_TO_CLIENTS: {
                const clientIds = parsed.slice(2, parsed.length - 1);
                const payload = parsed[parsed.length-1];

                this.onServerClientsRelayMessage?.(server, clientIds, payload);
                break;
            }
            case SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE_FAST_RELAY_TO_CLIENT: {
                const clientId = parsed[2];
                this.onServerClientRelayMessage?.(server, clientId, parsed[3]);
                break;
            }
            case SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE_FAST_RELAY_TO_AREA: {
                this.onServerRelayByAreaMessage?.(server, parsed[2]);
                break;
            }
            case SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE_FAST_RELAY_TO_CHANNEL: {
                // prefix carried [protocol, id, channelIndex]; payload is the
                // appended final frame.
                const channelIndex = parsed[2];
                const payload = parsed[parsed.length - 1];
                this.onServerChannelRelayMessage?.(server, channelIndex, payload);
                break;
            }
            case SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE_FAST_RELAY_RAW_TO_AREA: {
                const payload = parsed[parsed.length - 1];
                this.onServerRawRelayMessage?.(server, payload);
                break;
            }
            case SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE_FAST_RELAY_TO_SERVER: {
                const msgData : any = parsed[2];
                this.onServerMessage(server, msgData);
                break;
            }
            case SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE: {
                const msgData : any = parsed[2];
                this.onServerMessage(server, msgData);
                break;
            }
            case SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_RESPONSE: {
                const seq  = parsed[2];
                const data = parsed[3];
                const error = parsed[4];
                const reqId = `${server.id}_${seq}`;
                const res = this.requestCbs[reqId];
                if(res) {
                    delete this.requestCbs[reqId];
                    res(data, error);
                }
                break;
            }
            case SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_REQUEST: {
                const seq = parsed[2];
                const data = parsed[3];
                try {
                    const res = await this.onServerRequest(server, data);
                    this.publish(id, [
                        SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_RESPONSE,
                        this.id,
                        seq,
                        res,
                    ])
                } catch (err) {
                    if(!this.closed) {
                        this.publish(id, [
                            SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_RESPONSE,
                            this.id,
                            seq,
                            null,
                            err instanceof Error ? err.message : String(err)
                        ]);
                    }
                }
                break;
            }
            case SERVER_SERVER_PROTOCOLS.IS_MULTIPLE_MESSAGES: {
                const data = parsed[2];
                data.forEach((d: any) => {
                    this.onServerMessage(server, d)
                    /*
                    switch(d[0]) {
                        case SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE:
                            this.onServerMessage(server, d[1])
                            break;
                        case SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_RESPONSE:
                            const seq  = parsed[2];
                            const data = parsed[3];
                            const error = parsed[4];
                            const reqId = `${server.id}_${seq}`;
                            const res = this.requestCbs[reqId];
                            if(res) {
                                delete this.requestCbs[reqId];
                                res(data, error);
                            }
                            break;
                    }
                    */
                });
                break;
            }
            case SERVER_SERVER_PROTOCOLS.MASTER_BROADCAST: {
                this.onMasterBroadcast?.(parsed[2])
                break;
            }
        }
    }

    private shouldPing(serverId: string | number) : boolean {
        const { lastSentPing, lastSentPong, lastReceivedPing } = this.serverConnections[serverId].heartbeats;
        if(!lastSentPing && !lastReceivedPing) {
            return !!this.serverConnections[serverId].preparedFor
        }
        return lastSentPong > lastSentPing && (Date.now() - lastSentPong) >= HEARTBEAT_TIMEOUT
    }

    private timeWaitingForPong(serverId: string | number): number {
        const { lastSentPing, lastReceivedPong } = this.serverConnections[serverId].heartbeats;
        if(lastSentPing > lastReceivedPong) {
            return Date.now() - lastSentPing;
        }
        return 0;
    }

    public doHeartbeat() {
        for(let i = 0; i < this.finalizedConnections.length; i++) {
            const key = this.finalizedConnections[i];
            if(this.shouldPing(key)) {
                this.sendPing(key);
            } else if (this.timeWaitingForPong(key) > HEARTBEAT_TIMEOUT) {
                console.error("Server:", this.config.id, "Timed out waiting for pong from server", this.serverConnections[key].id)
                this.removeConnection(this.serverConnections[key], RemoveConnectionReason.MISSED_HEARTBEAT);
            }
        }
    }

    private sendPong(serverId: string | number) {
        this.serverConnections[serverId].heartbeats.lastSentPong = Date.now();
        const resArray = [
            SERVER_SERVER_PROTOCOLS.HEARTBEAT_PONG,
            this.id,
        ];
        if(this.onHeartbeat) {
            resArray.push(this.onHeartbeat());
        }
        this.publish(serverId, resArray)
    }

    private sendPing(serverId: string | number) {
        this.serverConnections[serverId].heartbeats.lastSentPing = Date.now();
        const resArray = [
            SERVER_SERVER_PROTOCOLS.HEARTBEAT_PING,
            this.id,
        ];
        if(this.onHeartbeat) {
            resArray.push(this.onHeartbeat());
        }
        this.publish(serverId, resArray)
    }

    public async requestServer(serverId: string | number, payload: any) : Promise<any> { 
        if(this.localServers[serverId]) {
            //console.log('server was local', serverId, payload);
            return this.localServers[serverId].onServerRequest(this.sharedConfig, payload);
        }
        const seq = this.seq++;
        if(this.seq > 65534) {
            this.seq = 0;
        }
        
        this.publish(serverId, [
            SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_REQUEST,
            this.id,
            seq,
            payload
        ]);
        return new Promise((resolve, reject) => {
            const reqId = `${serverId}_${seq}`;
            this.requestCbs[reqId] = (payload, error?: string) => {
                if(error) {
                    reject(error)
                } else {
                    resolve(payload)
                }
            }
            const timer = setTimeout(() => {
                // drop the callback so a late response can't invoke a settled promise
                delete this.requestCbs[reqId];
                return reject(new Error(`Timed out. (request ${this.id} → ${serverId}, protocol ${Array.isArray(payload) ? payload[0] : '?'})`));
            }, this.requestTimeout);
            (timer as any).unref?.();
        });
     }

    public abstract onServerRequest(server: BaseServerConfig<any>, payload: any) : Promise<any>

    public abstract onServerMessage(server: BaseServerConfig<any>, msg: any): void;
    public onServerClientRelayMessage?(server: BaseServerConfig<any>, clientId: string, payload: string): void
    public onServerRelayByAreaMessage?(server: BaseServerConfig<any>, payload: string): void
    public onServerClientsRelayMessage?(server: BaseServerConfig<any>, clientIds: string[], payload: string): void
    public onServerChannelRelayMessage?(server: BaseServerConfig<any>, channelIndex: number, payload: string): void
    public onServerRawRelayMessage?(server: BaseServerConfig<any>, payload: string): void

    public publish(serverId: string | number, data: Array<any>) {
        if(this.localServers[serverId]) {
            this.localServers[serverId].onServerMessage(this.sharedConfig, data);
        } else {
            this.pubSub?.publish(serverId, data)
        }
    }

    public relayToClient(serverId: string | number, clientId: string, payload: string) {
        if (this.localServers[serverId]) {
            this.localServers[serverId].onServerClientRelayMessage?.(this.sharedConfig, clientId, payload)
        } else if(this.serverConnections[serverId]) {
            this.pubSub?.publishRelay(serverId, [
                SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE_FAST_RELAY_TO_CLIENT,
                this.id,
                clientId,
            ], payload);
        }
    }


    public relayByAreaId(serverId: string, payload: string) {
        if (this.localServers[serverId]) {
            this.localServers[serverId].onServerRelayByAreaMessage?.(this.sharedConfig, payload)
        } else if(this.serverConnections[serverId]) {
            this.pubSub?.publishRelay(serverId, [
                SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE_FAST_RELAY_TO_AREA,
                this.id,
            ], payload);
        }
    }

    public relayToClients(serverId: string | number, clientIds: string[], payload: string) {
        if (this.localServers[serverId]) {
            this.localServers[serverId].onServerClientsRelayMessage?.(this.sharedConfig, clientIds, payload)
        } else if(this.serverConnections[serverId]) {
            this.pubSub?.publishRelay(serverId, [
                SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE_FAST_RELAY_TO_CLIENTS,
                this.id,
                ...clientIds,
            ], payload);
        }
    }

    public relayToChannel(serverId: string | number, channelIndex: number, payload: string) {
        if (this.localServers[serverId]) {
            this.localServers[serverId].onServerChannelRelayMessage?.(this.sharedConfig, channelIndex, payload)
        } else if(this.serverConnections[serverId]) {
            this.pubSub?.publishRelay(serverId, [
                SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE_FAST_RELAY_TO_CHANNEL,
                this.id,
                channelIndex,
            ], payload);
        }
    }

    public relayRawToArea(serverId: string | number, payload: string) {
        if (this.localServers[serverId]) {
            this.localServers[serverId].onServerRawRelayMessage?.(this.sharedConfig, payload)
        } else if(this.serverConnections[serverId]) {
            this.pubSub?.publishRelay(serverId, [
                SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE_FAST_RELAY_RAW_TO_AREA,
                this.id,
            ], payload);
        }
    }

    public sendToServer(serverId: string | number, payload: any) {
        if (this.localServers[serverId]) {
            this.localServers[serverId].onServerMessage(this.sharedConfig, payload)
        } else if(this.serverConnections[serverId]) {
            this.pubSub?.publish(serverId, [
                SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE,
                this.id,
                payload
            ]);
        } 
    }

    public sendToServers(serverIds: (string | number)[], payload: any) {
        const nonLocalServerIds = [];
        for(let i = 0; i < serverIds.length; i++) {
            const serverId = serverIds[i];
            const localServer = this.localServers[serverId];
            if(localServer) {
                localServer.onServerMessage(this.sharedConfig, payload)
            } else {
                nonLocalServerIds.push(serverId);
            }
        }
        if(nonLocalServerIds.length) {
            const stringifiedPayload = JSON.stringify([
                SERVER_SERVER_PROTOCOLS.SERVER_TO_SERVER_MESSAGE,
                this.id,
                payload
            ]);
            for(let i = 0; i < nonLocalServerIds.length; i++) {
                this.pubSub?.publishNoEncode(nonLocalServerIds[i], stringifiedPayload);
            }
        }
    }

    public sendToServerNoEncode(serverId: string | number, payload: string) {
        if (this.localServers[serverId]) {
            throw new Error(`Do not use no encode for local servers...`)
        } else if(this.serverConnections[serverId]) {
            this.pubSub?.publishNoEncode(serverId, payload);
        } 
    }
  
    /**
     * Pre-admit peers this server is about to register with (descriptors from
     * an authenticated source, e.g. the master's AREA_PREPARE reply), so their
     * prepares can dial this server's publisher before it connects back.
     */
    public async admitPeers(descriptors: Array<BaseServerConfig<any>>): Promise<void> {
        await this.pubSub?.admitPeers(descriptors.map(d => d?.publicKey).filter((k): k is string => !!k));
    }

    public async disconnectServer(d: BaseServerConfig<any>) {
        if(d.id in this.serverConnections) {
            await this.removeConnection(this.serverConnections[d.id], RemoveConnectionReason.STOPPED);
        }
    }

    private async removeConnection(d: BaseServerConfig<any>, reason: RemoveConnectionReason) {
        const data = this.serverConnections[d.id];
        delete this.serverConnections[d.id];
        const wasLocal = d.id in this.localServers;
        const idx = this.finalizedConnections.indexOf(d.id);
        if(idx > -1)  {
            this.finalizedConnections.splice(idx, 1);
        }
        if(wasLocal) {
            const localServer = this.localServers[d.id];
            delete this.localServers[d.id];
            // also drop the reciprocal side so both peers fire their close cleanup
            if(localServer && localServer.serverConnections[this.config.id]) {
                localServer.removeConnection(this.sharedConfig, reason);
            }
        }
        if (data) {
            if(this.pubSub && !wasLocal) {
                await this.pubSub.removeConnection(d);
            }
            this.connectionCloseListeners.forEach(l => l(data, reason));
        }
    }


    get isTestArea() {
        return (this.config.id === "test_area_0");
    }

    /**
     * The fields that ARE a connection's identity. Over the wire they come from
     * the entry this side prepared (or the descriptor it registered against) —
     * never from what the peer declares about itself, which is why a registrant
     * could once promote itself to any role by sending a different `type`.
     */
    private static readonly IDENTITY_FIELDS = ['id', 'type', 'host', 'port', 'publicKey', 'clusterId'] as const;

    /**
     * Check a peer's self-description against the entry this side holds for it.
     * Returns the first disagreement, or null. `id`/`type`/`host`/`publicKey`
     * and the cluster must match; the prepared entry is the authority.
     */
    private identityMismatch(prepared: BaseServerConfig<any>, declared: BaseServerConfig<any>): string | null {
        if(`${declared?.id}` !== `${prepared.id}`) return `declared id '${declared?.id}' but was prepared as '${prepared.id}'`;
        if(declared.type !== prepared.type) return `declared type ${SERVER_TYPES[declared.type] ?? declared.type} but was prepared as ${SERVER_TYPES[prepared.type] ?? prepared.type}`;
        if(declared.host !== prepared.host) return `declared host '${declared.host}' but was prepared at '${prepared.host}'`;
        if(declared.publicKey !== prepared.publicKey) return `declared a different public key than the one it was prepared with`;
        const declaredCluster = declared.clusterId || DEFAULT_CLUSTER_ID;
        if(declaredCluster !== this.clusterId) return `belongs to cluster '${declaredCluster}', not '${this.clusterId}'`;
        return null;
    }

    finalizeConnection(d: BaseServerConfig<any>, heartbeatState?: any, isLocal?: boolean) {
        const prior = this.serverConnections[d.id];
        // Over the wire the prepared/descriptor entry is authoritative for the
        // identity fields; in-process (`addLocalServer`) `d` IS the peer's own
        // sharedConfig, handed over by code in this process.
        const pinned: Partial<BaseServerConfig<any>> = {};
        if(prior && !isLocal) {
            for(const k of BaseServer.IDENTITY_FIELDS) {
                if(prior[k] !== undefined) (pinned as any)[k] = prior[k];
            }
        }
        const finalData = {
            ...prior,
            ...d,
            ...pinned,
            status: ServerConnectionStatus.CONNECTED,
        }

        this.finalizedConnections.push(d.id);

        if(!finalData.heartbeats) {
            finalData.heartbeats = {
                lastSentPing: 0,
                lastReceivedPing: 0,
                lastSentPong: 0,
                lastReceivedPong: 0,
            }
        }
        if(heartbeatState) {
            finalData.heartbeats.lastState = heartbeatState;
        }
        this.serverConnections[d.id] = finalData
        if(!this.isLocalServer(finalData.id) && !finalData.connectedCallback) {
            throw new Error(`Expected connected callback for server ${d.id}`);
        }
        finalData.connectedCallback?.(finalData);
        delete finalData.connectedCallback;
        this.connectionMadeListeners.forEach(l => l(finalData, !!isLocal));
    }

    
    public async prepareForServerRegistrationRequest(d: BaseServerConfig<any>, options: Partial<PrepareConnectionOptions>={...DefaultPrepareOptions}) : Promise<RegisteredServerData<any>> {
        options = { ...DefaultPrepareOptions, ...options };
        //console.log('prepareForServerRegistrationRequest', this.config.id, 'to', d.id);
        return new Promise(async (resolve, reject) => {
            if(d.id in this.serverConnections) {
                return reject(`Already connected to server ${d.id}`)
            }
            const refusal = this.descriptorRefusal(d);
            if(refusal) return reject(new Error(refusal));

            //console.log('TRYING TO CONNECT', this.config.id, 'to', d.id);
            await this.pubSub?.connect(d, true);
            // console.log('finish preparer connect', this.config.id, 'to', d.id);

            this.serverConnections[d.id] = {
                ...d, 
                preparedFor: true, 
                heartbeats: {
                    lastSentPing: 0,
                    lastReceivedPing: 0,
                    lastSentPong: 0,
                    lastReceivedPong: 0,
                },
                status: ServerConnectionStatus.AWAITING_REGISTRATION_REQUEST,
                connectedCallback: (d2: RegisteredServerData<any>) => {
                    //console.log('CONNECTED CALLBACK IN PREPARER', this.config.id, 'to', d.id);
                    resolve(d2);
                }
            }
      
            setTimeout(() => {
                if(this.serverConnections[d.id]?.status === ServerConnectionStatus.AWAITING_REGISTRATION_REQUEST) {
                    console.error("prepareForServerRegistrationRequest: Timed out waiting for server registration request.")
                    return reject(`Timed out waiting for server registration request from ${d.id} at host ${d.host}`);
                }
            }, options.timeout)
            /*
            await asyncTimeout(adjustedRetryTimeout(retry, 5000, MAX_RETRIES, 60000));
            if(++retry <= MAX_RETRIES) {
                if(this.serverConnections[d.id]?.status === ServerConnectionStatus.AWAITING_REGISTRATION_REQUEST) {
                    await this.pubSub?.removeConnection(d);
                    delete this.serverConnections[d.id]
                   // console.log('about to retry prepareForServerRegistrationRequest', retry);
                    return resolve(this.prepareForServerRegistrationRequest(d, retry));
                }
            } else {
                if(this.serverConnections[d.id]?.status === ServerConnectionStatus.AWAITING_REGISTRATION_REQUEST) {
                    this.removeConnection(d, RemoveConnectionReason.FAILED_TO_REGISTER)
                    console.error("prepareForServerRegistrationRequest: Failed to register and timing out.")
                    return reject(`Retried 5 times and failed to register...`);
                }
            }
            */
        });
    }

    public async sendRegistrationRequest(d: BaseServerConfig<any>) : Promise<RegisteredServerData<any>> {
        return new Promise(async (resolve, reject) => {
            let retry = 0;
            const MAX_RETRIES = 10;
            
            if(d.id in this.serverConnections) {
                return reject(`Already have open connection to server ${d.id}`)
            }
            const refusal = this.descriptorRefusal(d);
            if(refusal) return reject(new Error(refusal));

           //console.log('START send connect', this.config.id, 'to', d.id);
            await this.pubSub?.connect(d);
           //console.log('FINISH send connect', this.config.id, 'to', d.id);

            this.serverConnections[d.id] = {
                ...d,
                preparedFor: false,
                heartbeats: {
                    lastSentPing: 0,
                    lastSentPong: 0,
                    lastReceivedPing: 0,
                    lastReceivedPong: 0
                },
                status: ServerConnectionStatus.PENDING_REGISTRATION_CONFIRMATION,
                connectedCallback: (d2: RegisteredServerData<any>) => {
                   //console.log('IN SEND REGISTRATION REQUEST CALLBACK', this.config.id, 'to', d.id, d2);
                    resolve(d2);
                }
            };

            const sendRegistrationPub = () => {
               ////console.log(`sending reg pub from ${this.config.id} to ${d.id}: try#${retries}`)
             
                // `sharedConfig`, NEVER `this.config` — this publishes THIS
                // server's own config to a peer over a plain tcp:// socket. See
                // `sharedConfig`.
                this.pubSub?.publish(d.id, [
                    SERVER_SERVER_PROTOCOLS.REGISTER_SERVER,
                    this.id,
                    this.sharedConfig,
                    this.heartbeatData,
                ]);
            }

            const isStillWaiting = () => {
                return this.serverConnections[d.id]?.status === ServerConnectionStatus.PENDING_REGISTRATION_CONFIRMATION;
            }
           
            /*
            const onAccept = (e: { address: string }) => {
                console.log('ON ACCEPT', e, { address })
                if(e.address === address) {
                    console.log('ACCEPTED!!!!!!!!!!!! ping!!!');
                    
                    this.pubSub.publisher.events.off('accept', onAccept);
                }
            }
            this.pubSub.publisher.events.on('accept', onAccept);
            */
            sendRegistrationPub();
            // FAST PHASE: with CurveZMQ every new link is a handshake that
            // includes a ZAP round-trip through this process's event loop, so
            // the preparer's subscriber is often a few ms short of subscribed
            // when the first REGISTER goes out — a dropped first publish then
            // cost the full ~1.6s first slot below. REGISTER is idempotent, so
            // two quick re-sends close that window before the normal schedule.
            for (const fastDelay of [100, 300]) {
                await asyncTimeout(fastDelay);
                if(!isStillWaiting()) return;
                sendRegistrationPub();
            }
      
            while(++retry <= MAX_RETRIES && isStillWaiting()) {
                //console.log('sent registration pub...')

                await asyncTimeout(adjustedRetryTimeout(retry, 1500, MAX_RETRIES, 3000));
                //console.log("after first timeout....")

                if(!isStillWaiting()) {
                    return;
                }
                sendRegistrationPub();
            }
            //console.log("idek")
            if(isStillWaiting()) {
                try {
                    await this.pubSub?.removeConnection(d);
                } catch (err) {}
                return reject(`Timed out trying to send registration from ${this.config.id} to server: ${d.id}`)
            }
            /*
            if(++retry <= MAX_RETRIES) {
                if(this.serverConnections[d.id]?.status === ServerConnectionStatus.PENDING_REGISTRATION_CONFIRMATION) {
                    await this.pubSub.removeConnection(d);
                    await asyncTimeout(1000);

                    delete this.serverConnections[d.id]
                    // console.log('about to retry sendRegistrationRequest', retry);
                    return resolve(this.sendRegistrationRequest(d, retry));
                }
            }
            */
        });
    }
   
    /**
     * A peer descriptor must name a key (the pin) and must not belong to another
     * cluster. Checked before any socket is touched.
     */
    private descriptorRefusal(d: BaseServerConfig<any>): string | null {
        if(this.config.inProcessOnly) return null;
        if(!isCurveKey(d?.publicKey)) {
            return `Cannot connect ${this.id} to server '${d?.id}': the descriptor carries no public key. Cluster trust pins every peer by its CurveZMQ key — pass the peer's sharedConfig (or a descriptor with publicKey).`;
        }
        if(d.clusterId && d.clusterId !== this.clusterId) {
            return `Cannot connect ${this.id} to server '${d.id}': it belongs to cluster '${d.clusterId}', not '${this.clusterId}'.`;
        }
        return null;
    }

    onConfirmRegistration(d: BaseServerConfig<any>, heartbeatState?: any) {
        const pending = this.serverConnections[d?.id];
        if(pending?.status === ServerConnectionStatus.PENDING_REGISTRATION_CONFIRMATION) {
            const mismatch = this.identityMismatch(pending, d);
            if(mismatch) {
                this.refuse(`registration confirmation from '${d.id}' ${mismatch}.`);
                return;
            }
            this.finalizeConnection(d, heartbeatState);
            delete this.serverConnections[d.id].connectedCallback;
        }
    }

    onNewRegisterServer(d: BaseServerConfig<any>, heartbeatState?: any, retry: number=0): void | NodeJS.Timeout {
        if(!(d.id in this.serverConnections) && retry < 100) {
            return setTimeout(() => {
                this.onNewRegisterServer(d, heartbeatState, ++retry)
            }, 200);
        }
        // Give-up and wrong-status paths must be LOUD. This used to fall
        // through silently after 100 x 200ms, which is precisely how a wedged
        // prepare (e.g. a hung zmq bind stalling `pubSub.connect`) turned into
        // an unexplainable 23s registration timeout on the OTHER side of the
        // socket with zero local evidence.
        if(!(d.id in this.serverConnections)) {
            console.error(`[${this.config.id}] REGISTER_SERVER from '${d.id}' arrived but no prepared connection appeared within 20s — the prepare side never completed (wedged pubSub.connect / hung bind?). Dropping the registration; the peer will time out.`);
            return;
        }
        if(this.serverConnections[d.id]?.status === ServerConnectionStatus.AWAITING_REGISTRATION_REQUEST) {
            // The PREPARED entry is the authority on who this peer is. A
            // registrant (authenticated by its key, but possibly misconfigured
            // or hostile) that describes itself differently is refused — its
            // own type/id/host never overwrite what was prepared.
            const mismatch = this.identityMismatch(this.serverConnections[d.id], d);
            if(mismatch) {
                this.refuse(`registration from '${d.id}' ${mismatch}.`);
                return;
            }
            this.finalizeConnection(d, heartbeatState);
            // `sharedConfig`, NEVER `this.config` — the confirmation carries
            // this server's own config back to the requester. See `sharedConfig`.
            this.pubSub?.publish(d.id, [
                SERVER_SERVER_PROTOCOLS.CONFIRM_REGISTRATION,
                this.id,
                this.sharedConfig,
                this.heartbeatData,
            ])
        }
    }
}
